/**
 * @file BookingFlow.jsx — multi-step booking UI.
 */
import React, { useState, useRef, useEffect } from "react";
import {
  CalendarRange,
  Clock,
  MapPin,
  Video,
  CheckCircle2,
  Bell,
  User,
  Stethoscope,
} from "lucide-react";
import { PUBLIC_STRINGS, WAITLIST_STRINGS } from "../constants/strings";
import { Button, Card, Checkbox, Chip, Spinner, Textarea } from "../ui";
import "./BookingFlow.css";

const formatDateLabel = (dateStr) => {
  const date = new Date(`${dateStr}T12:00:00`);
  return date.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" });
};

const getNextMonths = (n = 6) => {
  const months = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
    months.push({ value, label });
  }
  return months;
};

const NEXT_MONTHS = getNextMonths(6);

function ConfirmationCard({ confirmedBooking, isPro }) {
  return (
    <Card className="bf-confirm">
      <div className="bf-confirm-head">
        <CheckCircle2 size={32} className="bf-confirm-icon" />
        <div>
          <h3 className="bf-confirm-title">¡Cita agendada con éxito!</h3>
          <p className="bf-confirm-sub">Tu cita ha sido registrada</p>
        </div>
      </div>
      <ul className="bf-confirm-list">
        <li className="bf-confirm-row">
          <User size={14} />
          <div>
            <span className="bf-confirm-label">Profesional</span>
            <strong>{confirmedBooking.professionalName}</strong>
            {confirmedBooking.specialties?.length > 0 && (
              <div className="bf-confirm-chips">
                {confirmedBooking.specialties.map((name) => (
                  <Chip key={name} tone="primary" size="sm">
                    {name}
                  </Chip>
                ))}
              </div>
            )}
          </div>
        </li>
        <li className="bf-confirm-row">
          <CalendarRange size={14} />
          <div>
            <span className="bf-confirm-label">Fecha y hora</span>
            <strong className="bf-capitalize">{formatDateLabel(confirmedBooking.date)}</strong>
            <span className="bf-confirm-aside">
              {confirmedBooking.startTime} – {confirmedBooking.endTime}
            </span>
          </div>
        </li>
        {confirmedBooking.service && (
          <li className="bf-confirm-row">
            <Stethoscope size={14} />
            <div>
              <span className="bf-confirm-label">Servicio</span>
              <strong>{confirmedBooking.service.name}</strong>
              {confirmedBooking.service.price != null && (
                <span className="bf-confirm-aside">
                  ${confirmedBooking.service.price.toLocaleString("es-MX")} {confirmedBooking.currency || "MXN"}
                </span>
              )}
            </div>
          </li>
        )}
        {confirmedBooking.location && (
          <li className="bf-confirm-row">
            <MapPin size={14} />
            <div>
              <span className="bf-confirm-label">Ubicación</span>
              <strong>
                {confirmedBooking.location.name || confirmedBooking.location.address}
              </strong>
              {confirmedBooking.location.name && (
                <span className="bf-confirm-aside">
                  {confirmedBooking.location.address}
                  {confirmedBooking.location.city ? `, ${confirmedBooking.location.city}` : ""}
                </span>
              )}
            </div>
          </li>
        )}
        {confirmedBooking.reason && (
          <li className="bf-confirm-row">
            <Clock size={14} />
            <div>
              <span className="bf-confirm-label">Motivo</span>
              <span>{confirmedBooking.reason}</span>
            </div>
          </li>
        )}
      </ul>
      <div className="bf-confirm-info">
        <Bell size={14} />
        <span>
          {isPro ? (
            <>
              Recibirás un recordatorio por WhatsApp el día anterior. En ese mensaje podrás{" "}
              <strong>confirmar tu asistencia</strong>.
            </>
          ) : (
            "Recibirás un recordatorio por correo electrónico el día anterior."
          )}
        </span>
      </div>
    </Card>
  );
}

export default function BookingFlow({
  professional,
  booking,
  onConfirm,
  isBooking,
  confirmedBooking,
  confirmButtonLabel,
  isPro = true,
  waitlist = null,
  mode = "public",
}) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [reason, setReason] = useState("");
  const [forSomeoneElse, setForSomeoneElse] = useState(false);
  const [attendeeName, setAttendeeName] = useState("");

  const serviceRef = useRef(null);
  const monthRef = useRef(null);
  const dayRef = useRef(null);
  const slotRef = useRef(null);
  const formRef = useRef(null);

  const {
    selectedLocation,
    handleLocationSelect,
    selectedService,
    handleServiceSelect,
    selectedMonth,
    setSelectedMonth,
    availableDates,
    datesLoading,
    selectedDate,
    setSelectedDate,
    slots,
    slotsLoading,
    nextAvailable,
    nextAvailableLoading,
  } = booking;

  const hasMultipleLocations = professional?.locations?.length > 1;
  const allServices = professional?.consultationServices || professional?.services || [];
  const activeServices = allServices.filter((s) => s.isActive);
  const hasMultipleServices = activeServices.length > 1;

  useEffect(() => {
    if (selectedLocation) setTimeout(() => serviceRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [selectedLocation?.id]);
  useEffect(() => {
    if (selectedService) setTimeout(() => monthRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [selectedService?.id]);
  useEffect(() => {
    if (selectedMonth) setTimeout(() => dayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [selectedMonth]);
  useEffect(() => {
    if (selectedDate) setTimeout(() => slotRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [selectedDate]);
  useEffect(() => {
    if (selectedSlot) setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [selectedSlot]);

  const handleConfirm = () => {
    if (!selectedSlot) return;
    if (forSomeoneElse && !attendeeName.trim()) return;
    onConfirm({ slot: selectedSlot, reason, attendeeName: forSomeoneElse ? attendeeName.trim() : null });
  };

  if (confirmedBooking) {
    return (
      <>
        <ConfirmationCard confirmedBooking={confirmedBooking} isPro={isPro} />
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Button asChild>
            <a href={mode === "professional" ? "/citas" : "/mis-citas"}>
              {mode === "professional" ? "Ver citas" : "Ver mis citas"}
            </a>
          </Button>
        </div>
      </>
    );
  }

  return (
    <Card padded={false} className="bf-card">
      <div className="bf-head">
        <h2 className="bf-head-title">Agendar cita</h2>
        <p className="bf-head-sub">Selecciona ubicación, fecha y horario.</p>
      </div>
      <div className="bf-body">
        {hasMultipleLocations && (
          <section className="bf-section">
            <h3 className="bf-section-title">
              <MapPin size={14} /> Selecciona una ubicación
            </h3>
            <div className="bf-options">
              {professional.locations.map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  className={`bf-option ${selectedLocation?.id === loc.id ? "bf-option-on" : ""}`}
                  onClick={() => handleLocationSelect(loc)}
                >
                  <div className="bf-option-head">
                    {loc.isVirtual ? <Video size={14} /> : <MapPin size={14} />}
                    <strong>{loc.name || loc.address}</strong>
                  </div>
                  {!loc.isVirtual && (loc.address || loc.colonia || loc.city) && (
                    <span className="bf-option-sub">
                      {[loc.address, loc.colonia, loc.city, loc.state].filter(Boolean).join(", ")}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        {!hasMultipleLocations && professional?.locations?.length === 1 && (
          <div className="bf-info">
            {professional.locations[0].isVirtual ? <Video size={14} /> : <MapPin size={14} />}
            <span>
              {professional.locations[0].isVirtual
                ? professional.locations[0].name || "Teleconsulta"
                : [
                    professional.locations[0].name,
                    professional.locations[0].address,
                    professional.locations[0].colonia,
                    professional.locations[0].city,
                    professional.locations[0].state,
                  ]
                    .filter(Boolean)
                    .join(", ")}
            </span>
          </div>
        )}

        {selectedLocation && hasMultipleServices && (
          <section ref={serviceRef} className="bf-section">
            <h3 className="bf-section-title">
              <Stethoscope size={14} /> {PUBLIC_STRINGS.SELECT_SERVICE}
            </h3>
            <div className="bf-options">
              {activeServices.map((svc) => (
                <button
                  key={svc.id}
                  type="button"
                  className={`bf-option ${selectedService?.id === svc.id ? "bf-option-on" : ""}`}
                  onClick={() => handleServiceSelect(svc)}
                >
                  <div className="bf-option-head">
                    <strong>{svc.name}</strong>
                    {svc.price != null && (
                      <span className="bf-option-price">
                        ${svc.price.toLocaleString("es-MX")} {professional.currency || "MXN"}
                      </span>
                    )}
                  </div>
                  {svc.description && <span className="bf-option-sub">{svc.description}</span>}
                </button>
              ))}
            </div>
          </section>
        )}

        {selectedLocation && !hasMultipleServices && activeServices.length === 1 && (
          <div ref={serviceRef} className="bf-info">
            <Stethoscope size={14} />
            <span>
              {activeServices[0].name}
              {activeServices[0].price != null &&
                ` · $${activeServices[0].price.toLocaleString("es-MX")} ${professional.currency || "MXN"}`}
            </span>
          </div>
        )}

        {selectedLocation && selectedService && waitlist?.enabled && (
          <Card className="bf-waitlist">
            {nextAvailableLoading ? (
              <div style={{ textAlign: "center", padding: 12 }}>
                <Spinner size={20} />
              </div>
            ) : (
              <>
                {nextAvailable ? (
                  <div className="bf-waitlist-row">
                    <CalendarRange size={14} />
                    <span>
                      <strong>{WAITLIST_STRINGS.NEXT_AVAILABLE}</strong>{" "}
                      {formatDateLabel(nextAvailable.date)}
                      {nextAvailable.time && `, ${nextAvailable.time}`}
                    </span>
                  </div>
                ) : (
                  <div className="bf-waitlist-row">
                    <Bell size={14} />
                    <span>{WAITLIST_STRINGS.NO_AVAILABILITY_LONG}</span>
                  </div>
                )}
                {!waitlist.isJoined ? (
                  <>
                    <div className="bf-waitlist-divider" />
                    <strong>{WAITLIST_STRINGS.PROMPT}</strong>
                    <p>{WAITLIST_STRINGS.PROMPT_SUBTITLE}</p>
                    <div className="bf-pills">
                      {[7, 15, 30, 60].map((d) => (
                        <button
                          key={d}
                          type="button"
                          className={`bf-pill ${waitlist.durationDays === d ? "bf-pill-on" : ""}`}
                          onClick={() => waitlist.setDurationDays(d)}
                        >
                          {WAITLIST_STRINGS[`DURATION_${d}`]}
                        </button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<Bell size={14} />}
                      onClick={waitlist.join}
                      disabled={waitlist.isLoading}
                    >
                      {WAITLIST_STRINGS.SUBSCRIBE_BUTTON}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="bf-waitlist-divider" />
                    <div className="bf-waitlist-joined">
                      <CheckCircle2 size={14} />
                      <span>{WAITLIST_STRINGS.JOINED_TITLE}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={waitlist.leave}
                        disabled={waitlist.isLoading}
                        style={{ marginLeft: "auto" }}
                      >
                        {WAITLIST_STRINGS.LEAVE_BUTTON}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </Card>
        )}

        {selectedLocation && selectedService && (
          <section ref={monthRef} className="bf-section">
            <h3 className="bf-section-title">
              <CalendarRange size={14} /> Selecciona un mes
            </h3>
            <div className="bf-pills">
              {NEXT_MONTHS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={`bf-pill ${selectedMonth === m.value ? "bf-pill-on" : ""}`}
                  onClick={() => {
                    setSelectedMonth(m.value);
                    setSelectedDate(null);
                    setSelectedSlot(null);
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </section>
        )}

        {selectedMonth && (
          <section ref={dayRef} className="bf-section">
            <h3 className="bf-section-title">Selecciona un día</h3>
            {datesLoading ? (
              <Spinner size={22} />
            ) : availableDates === null || availableDates?.length === 0 ? (
              <div>
                <p className="bf-empty">Sin disponibilidad este mes en esta ubicación.</p>
                {waitlist?.enabled && !waitlist.isJoined && (
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<Bell size={14} />}
                    onClick={waitlist.join}
                  >
                    {WAITLIST_STRINGS.NO_AVAILABILITY_CTA}
                  </Button>
                )}
              </div>
            ) : (
              <div className="bf-pills">
                {availableDates.map((date) => (
                  <button
                    key={date}
                    type="button"
                    className={`bf-pill ${selectedDate === date ? "bf-pill-on" : ""}`}
                    onClick={() => {
                      setSelectedDate(date);
                      setSelectedSlot(null);
                    }}
                  >
                    {formatDateLabel(date)}
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {selectedDate && (
          <section ref={slotRef} className="bf-section">
            <h3 className="bf-section-title">
              <Clock size={14} /> {PUBLIC_STRINGS.SELECT_SLOT}
            </h3>
            {slotsLoading ? (
              <Spinner size={22} />
            ) : slots.length === 0 ? (
              <p className="bf-empty">{PUBLIC_STRINGS.NO_SLOTS}</p>
            ) : (
              <div className="bf-pills">
                {slots.map((slot) => (
                  <button
                    key={slot.startTime}
                    type="button"
                    className={`bf-pill ${selectedSlot?.startTime === slot.startTime ? "bf-pill-on" : ""}`}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    {slot.startTime}
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {selectedSlot && (
          <div ref={formRef} className="bf-form">
            <h3 className="bf-section-title">{PUBLIC_STRINGS.BOOKING_TITLE}</h3>
            <p className="bf-form-meta">
              {formatDateLabel(selectedDate)} — {selectedSlot.startTime} ({professional.sessionDuration} min)
            </p>
            {(selectedLocation || selectedService) && (
              <p className="bf-form-meta-sub">
                {[
                  selectedLocation && (selectedLocation.name || selectedLocation.address),
                  selectedService && selectedService.name,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                {selectedService?.price != null &&
                  ` · $${selectedService.price.toLocaleString("es-MX")} ${professional.currency || "MXN"}`}
              </p>
            )}
            <Textarea
              rows={3}
              label={PUBLIC_STRINGS.REASON_LABEL}
              placeholder={PUBLIC_STRINGS.REASON_PLACEHOLDER}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Checkbox
              label="La cita es para otra persona"
              checked={forSomeoneElse}
              onCheckedChange={(c) => {
                setForSomeoneElse(!!c);
                if (!c) setAttendeeName("");
              }}
            />
            {forSomeoneElse && (
              <div style={{ paddingLeft: 28 }}>
                <input
                  className="bf-input"
                  placeholder="Nombre del paciente — Ej. Juan Hernández Jr."
                  value={attendeeName}
                  onChange={(e) => setAttendeeName(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="bf-form-actions">
              <Button
                onClick={handleConfirm}
                loading={isBooking}
                disabled={forSomeoneElse && !attendeeName.trim()}
              >
                {confirmButtonLabel}
              </Button>
              <Button variant="ghost" onClick={() => setSelectedSlot(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export const buildConfirmationData = ({ professional, selectedLocation, selectedService, selectedDate, slot, reason }) => ({
  professionalName: [professional.title, professional.user.firstName, professional.user.lastName].filter(Boolean).join(" "),
  specialties: professional.specialties?.map((s) => s.name) || [],
  location: selectedLocation,
  service: selectedService,
  currency: professional.currency,
  date: selectedDate,
  startTime: slot.startTime,
  endTime: new Date(
    new Date(slot.dateTime).getTime() + professional.sessionDuration * 60 * 1000
  ).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false }),
  reason,
});
